<template>
  <div>
    <b-button
      :disabled="isButtonDisabled"
      id="btn_search"
      variant="primary"
      class="btn btn-label btn-primary float-right"
      @click="exportToExcel"
      ><i class="bx bx-export label-icon"></i>Export to Excel</b-button
    >
  </div>
</template>

<script>
import Vue from 'vue';
import * as XLSX from 'xlsx';
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
Vue.use(VueToast);

export default {
  name: 'ExportToExcel',
  components: {},
  props: {
    tableData: Array,
    summaryTableData: Array,
    itemizedSummaryTableData: Array,
    reportTotals: Array,
    productTableData: Array,
    productReportTotals: Array,
    summaryReport: Array,
    searchCustomer: String,
    searchJobNum: Number,
    searchStartDate: Date,
    searchEndDate: Date,
    searchEmployee: String,
    showProducts: Boolean,
    exportType: String,
  },
  data() {
    return {
      isButtonDisabled: true,
    };
  },
  methods: {
    exportToExcel() {
      try {
        if (this.exportType === 'employee_hours') {
          this.isButtonDisabled = true;
          //Times worksheet to Excel
          //format the column order.
          let formattedArray = this.formatBasicTable();
          let formattedSummaryArray = this.formatEmployeeHoursSummaryTable();
          //add the times to the worksheet
          const timesWorksheet = XLSX.utils.json_to_sheet(formattedArray);
          const summaryWorksheet = XLSX.utils.json_to_sheet(
            formattedSummaryArray
          );
          //create a new workbook
          const workbook = XLSX.utils.book_new();
          //add the worksheet to the workbook and call the sheet "times"
          XLSX.utils.book_append_sheet(workbook, timesWorksheet, 'Times');
          XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

          // set cells with numeric values to type numeric
          this.formatNumericExcelSheetCells(timesWorksheet);
          this.formatNumericExcelSheetCells(summaryWorksheet);

          //download the file.
          XLSX.writeFile(
            workbook,
            `${this.searchCustomer ? this.searchCustomer : ''}-${
              this.searchJobNum ? this.searchJobNum : ''
            }-${this.startDate ? this.startDate : ''}-${
              this.endDate ? this.endDate : ''
            }.xlsx`
          );
          this.isButtonDisabled = false;
        } else if (this.exportType === 'hours_worked') {
          this.isButtonDisabled = true;
          //Times worksheet to Excel
          //format column order
          let formattedArray = this.formatBasicTableHoursWorked();
          const timesWorksheet = XLSX.utils.json_to_sheet(this.tableData);

          // set cells with numeric values to type numeric
          this.formatNumericExcelSheetCells(timesWorksheet);

          const workbook = XLSX.utils.book_new();
          const colWidth = formattedArray.map((row) =>
            Object.values(row).map((val) =>
              val === null || val === undefined
                ? { wch: 10 }
                : {
                    wch:
                      val.toString().length > 13 ? val.toString().length : 13,
                  }
            )
          );

          let result = colWidth[0];
          for (let i = 1; i < colWidth.length; i++) {
            for (let j = 0; j < colWidth[i].length; j++) {
              if (result[j]['wch'] < colWidth[i][j]['wch']) {
                result[j]['wch'] = colWidth[i][j]['wch'];
              }
            }
          }
          timesWorksheet['!cols'] = result;

          XLSX.utils.book_append_sheet(workbook, timesWorksheet, 'Times');

          //Itemized summary worksheet to Excel
          if (this.itemizedSummaryTableData.length > 0) {
            let formattedTable = this.formatEmployeeHoursSummaryTable();
            const summaryWorksheet = XLSX.utils.json_to_sheet(formattedTable);
            summaryWorksheet['!cols'] = result;
            XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');
          }

          //create a deep copy of the table data, so it doesn't update the parent data.
          let sortedTimesByEmployeeAndDate = JSON.parse(
            JSON.stringify(this.tableData)
          );
          //sort the times by date and then employee
          sortedTimesByEmployeeAndDate = sortedTimesByEmployeeAndDate.sort(
            (a, b) => {
              if (a.Employee === b.Employee) {
                return b.Date - a.Date;
              }
              return a.Employee > b.Employee ? 1 : -1;
            }
          );

          //itterate through the array and sum up all hours for the day for each employee each day.
          let dailyHourTotal = 0;
          for (let i = 0; i <= sortedTimesByEmployeeAndDate.length - 1; i++) {
            if (i == 0) {
              dailyHourTotal = Number(sortedTimesByEmployeeAndDate[i].Hours);
            } else if (i == sortedTimesByEmployeeAndDate.length - 1) {
              //this is the final time, so we need to add the summary to the end.
              dailyHourTotal += Number(sortedTimesByEmployeeAndDate[i].Hours);

              sortedTimesByEmployeeAndDate.push({
                Hours: '',
                Customer: '',
                Date: '',
                Description: `Total hours for ${sortedTimesByEmployeeAndDate[i].Date}`,
                Employee: sortedTimesByEmployeeAndDate[i].Employee,
                JobNum: '',
                Rate: '',
                lineTotal: '',
                'Total Daily Hours': dailyHourTotal,
              });
              break;
            } else {
              //if the name and the date are the same, we need to add the hours to subtotal.
              if (
                sortedTimesByEmployeeAndDate[i - 1].Employee ==
                  sortedTimesByEmployeeAndDate[i].Employee &&
                sortedTimesByEmployeeAndDate[i - 1].Date ==
                  sortedTimesByEmployeeAndDate[i].Date
              ) {
                //add the time
                dailyHourTotal += Number(sortedTimesByEmployeeAndDate[i].Hours);
              } else {
                //we are on a new date or employee, add the previous total to the data and reset the daily hour total
                sortedTimesByEmployeeAndDate.splice(i, 0, {
                  Hours: '',
                  Customer: '',
                  Date: '',
                  Description: `Total hours for ${
                    sortedTimesByEmployeeAndDate[i - 1].Date
                  }`,
                  Employee: sortedTimesByEmployeeAndDate[i - 1].Employee,
                  JobNum: '',
                  Rate: '',
                  lineTotal: '',
                  'Total Daily Hours': dailyHourTotal,
                });

                i = i + 1;

                dailyHourTotal = Number(sortedTimesByEmployeeAndDate[i].Hours);
              }
            }
          }

          //Sorted times worksheet to Excel
          const sortedTimesWorksheet = XLSX.utils.json_to_sheet(
            sortedTimesByEmployeeAndDate
          );

          // set cells with numeric values to type numeric
          this.formatNumericExcelSheetCells(sortedTimesWorksheet);

          XLSX.utils.book_append_sheet(
            workbook,
            sortedTimesWorksheet,
            'Daily Summary Times'
          );

          XLSX.writeFile(
            workbook,
            `${this.searchCustomer ? this.searchCustomer : ''}-${
              this.searchJobNum ? this.searchJobNum : ''
            }-${this.startDate ? this.startDate : ''}-${
              this.endDate ? this.endDate : ''
            }.xlsx`
          );

          this.isButtonDisabled = false;
        }
      } catch (err) {
        console.log('Error generating excel report', err);
      }
    },

    formatSummaryTable() {
      try {
        let returnArray = this.summaryTableData.map((data) => {
          return {
            employee_number: data.employeeID,
            amount: data.valueData.total,
            earning_code: data.valueName,
            exception_rate: 1,
            for_pay_period_ending: this.searchEndDate ? this.searchEndDate : '',
            hours: data.valueData.value,
            applicable_period: '',
            department: '',
            ID_X: '',
            ID_Y: '',
            ID_Z: '',
          };
        });
        return returnArray;
      } catch (err) {
        console.log(err);
        Vue.$toast.open({
          message: `Error parsing summary report. Please contact support.`,
          duration: 8000000,
          position: 'top-right',
          dismissible: true,
          type: 'error',
        });
      }
    },

    formatEmployeeHoursSummaryTable() {
      //we will be getting a random amount of products here and we won't know the names. This needs to be dynamically generated.
      //loop over the summary table data and then itterate the object in each array item.
      let returnArray = [];
      this.summaryTableData.forEach((item) => {
        let newArrayItem = {};
        //loop over the object. We are checking for the name and id because the name is a key string and the rest are key objects that need to be
        //reformatted. We aren't interested in storing the id field.
        for (const key in item) {
          if (key == 'name') {
            newArrayItem[key] = item[key];
          } else if (key != 'id') {
            newArrayItem[key] = item[key].value;
          }
        }
        returnArray.push(newArrayItem);
      });

      return returnArray;
    },

    formatBasicTable() {
      let returnArray = this.tableData.map((data) => {
        return {
          date: data.Date,
          employee: data.Employee,
          customer: data.Customer,
          job: data.Job,
          description: data.Description,
          hours: data.Hours,
          rate: data.Rate,
          lineTotal: data.lineTotal,
        };
      });
      return returnArray;
    },

    formatNumericExcelSheetCells(cellArray) {
      for (const cell in cellArray) {
        let cellValue = cellArray[cell].v;
        if (
          !isNaN(cellValue) &&
          typeof cellValue !== 'boolean' &&
          typeof cellValue !== 'undefined' &&
          typeof cellValue !== 'object'
        ) {
          cellArray[cell].t = 'n';
        }
      }

      return cellArray;
    },
    formatBasicTableHoursWorked() {
      let returnArray = this.tableData.map((data) => {
        delete data.id;
        return {
          Approved: data.approved,
          ApprovedBy: data.approvedBy ? data.approvedBy.name : '',
          ApprovedTime: data.approvedTime,
          JobNum: data.JobNum,
          Customer: data.Customer,
          Date: data.Date,
          Employee: data.Employee,
          Description: data.Description,
          Billable: data.Billable,
          Hours: data.Hours,
          Type: data.Type,
          Rate: data.Rate,
          LineTotal: data.lineTotal,
        };
      });
      return returnArray;
    },
  },
  computed: {
    startDate() {
      if (this.searchStartDate) {
        return this.searchStartDate.toString().substring(0, 15);
      } else {
        return null;
      }
    },
    endDate() {
      if (this.searchEndDate) {
        return this.searchEndDate.toString().substring(0, 15);
      } else {
        return null;
      }
    },
  },
  watch: {
    tableData: function(val) {
      if (val.length > 0) {
        this.isButtonDisabled = false;
      } else {
        this.isButtonDisabled = true;
      }
    },
    itemizedSummaryTableData: function(val) {
      if (val.length > 0) {
        this.isButtonDisabled = false;
      } else {
        this.isButtonDisabled = true;
      }
    },
    searchJobNum: function() {
      this.isButtonDisabled = true;
    },
    searchEmployee: function() {
      this.isButtonDisabled = true;
    },
    searchStartDate: function() {
      this.isButtonDisabled = true;
    },
    searchEndDate: function() {
      this.isButtonDisabled = true;
    },
    searchCustomer: function() {
      this.isButtonDisabled = true;
    },
  },
};
</script>

<style scoped></style>
